import { call, put, select } from 'redux-saga/effects';
import moment from 'moment';
import { ApiError, ApiResponse } from 'store/api/types';
import { logoutRequest } from 'store/auth/actions';
import { getAuthUser } from 'store/auth/selectors';
import { turnMessageOn } from 'store/app/actions';
import { DataLogStatus, SubmissionUpdateField } from 'store/app/enums';
import { MessageData } from 'store/app/types';
import { MessageStates } from 'containers/MessageBox';
import { updateSubmissionLog } from 'store/organisation/actions';
import { saveReturnFailure, saveReturnSuccess, setUpdateFromPageRequest } from 'store/vat/actions';
import { SaveReturnRequestAction } from 'store/vat/types';
import { checkResponse } from 'store/utils';
import { getUserMembers } from 'store/user/selectors';
import routes from 'routes';
import { isTifdReportPath } from 'utils/tifd-mapping';
import { renderErrorMessage } from 'utils/render-error-message';
import i18n from 'i18next';
import { saveTifdReport, updateTifdReport } from '../api';
import { previewTifdReportSuccess } from '../actions';
import { isTifdReportSaved, TifdReport } from '../models';
import { getTifdContext, getTifdReport } from '../selectors';

const GENERATED_DATE_FORMAT = 'DD/MM/YYYY HH:mm:ss';

const readReport = (response: ApiResponse): TifdReport | null => {
  const data = response?.data;
  if (data && typeof data === 'object' && 'report' in data && data.report) {
    return data.report as TifdReport;
  }
  if (data && typeof data === 'object' && Array.isArray((data as { sheets?: unknown }).sheets)) {
    return data as unknown as TifdReport;
  }
  return null;
};

const isoDate = (value: string | null | undefined): string => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const parsed = moment(value, ['YYYY-MM-DD', 'DD/MM/YYYY'], true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : value;
};

const generatedDateLabel = (
  report: TifdReport,
  fallback: string
): string => {
  if (report.generatedDate == null) return fallback;
  const parsed = moment.utc(report.generatedDate);
  return parsed.isValid() ? parsed.format(GENERATED_DATE_FORMAT) : fallback;
};

/**
 * Save from the TIFD report page. Returns true when this request belonged to
 * TIFD, including when the call failed. VAT save continues only when this
 * returns false.
 */
/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* saveTifdReportFromReturnSaga(action: SaveReturnRequestAction) {
  if (!isTifdReportPath(window.location.pathname)) return false;

  const { saveReturnRequestData } = action.payload;
  try {
    const report: TifdReport | null = yield select(getTifdReport);
    const context: ReturnType<typeof getTifdContext> = yield select(getTifdContext);
    const applicationConnectionId = context?.applicationConnectionId;
    const connectionId = context?.connectionId || saveReturnRequestData.connectionId;
    const mappingId = context?.mappingId || saveReturnRequestData.mappingId;
    if (!report || !applicationConnectionId) {
      throw new Error('TIFD report is not ready to save.');
    }

    const periodStart = isoDate(report.periodStart || saveReturnRequestData.fromDate);
    const periodEnd = isoDate(report.periodEnd || saveReturnRequestData.toDate);
    const roundingCode = report.roundingCode || undefined;
    const alreadySaved = isTifdReportSaved(report);

    const response: ApiResponse = alreadySaved
      ? yield call(
          updateTifdReport,
          applicationConnectionId,
          report.id as string,
          { refreshTrialBalance: false, roundingCode }
        )
      : yield call(saveTifdReport, applicationConnectionId, {
          periodStart,
          periodEnd,
          roundingCode,
        });

    const err: ApiError | null = checkResponse(response?.responseMessage);
    if (err) throw err;

    const saved = readReport(response);
    if (!saved?.id) {
      throw new Error('The save response did not include a report.');
    }

    yield put(
      previewTifdReportSuccess(saved, {
        connectionId,
        mappingId,
        applicationConnectionId,
      })
    );

    const currentUser = yield select(getAuthUser);
    const users = yield select(getUserMembers);
    const generatedDate = generatedDateLabel(
      saved,
      saveReturnRequestData.generatedDate
    );
    yield put(saveReturnSuccess(saved.id, users, currentUser, generatedDate, false));

    if (!alreadySaved) {
      window.history.pushState(
        {},
        '',
        routes.vat.tifdViewReportLink
          .replace(':connectionId', saveReturnRequestData.connectionId)
          .replace(':mappingId', saveReturnRequestData.mappingId)
          .replace(':dataLogId', saved.id)
      );
    }

    const message: MessageData = {
      title: i18n.t('common.success-exclamation'),
      description: response.responseMessage?.message,
      type: MessageStates.SUCCESS,
    };
    yield put(turnMessageOn(message));
    yield put(
      updateSubmissionLog(
        saveReturnRequestData.mappingId,
        SubmissionUpdateField.STATUS,
        DataLogStatus.SAVED,
        saved.id
      )
    );
    yield put(setUpdateFromPageRequest(false));
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else if (error?.message) {
      yield put(
        turnMessageOn({
          title: i18n.t('common.error'),
          description: error.message,
          type: MessageStates.ERROR,
        })
      );
    } else {
      yield put(logoutRequest());
    }
    yield put(saveReturnFailure());
    yield put(setUpdateFromPageRequest(false));
  }

  return true;
}
